import React, { useState, useCallback, useEffect } from "react";
import { ApiHook } from "../../hooks/apiHook";
import { Button, Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { Card, Alert, Spinner, Modal, Form, InputGroup } from "react-bootstrap";
import { useDropboxFolder } from "./useDropboxFolder";
import FolderIcon from "@mui/icons-material/Folder";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import MovieIcon from "@mui/icons-material/Movie";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import API from "../../api/api";

export default function PDF() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const folders = ApiHook.Calllms();
  const templatesFolder = folders?.data?.data?.data?.find(
    (folder) => folder.title === "Templates"
  );
  const templatesFolderId =
    "https://www.dropbox.com/scl/fo/306303rmh1u3gixnu89mq/AKk_48ON0UAaU4ZyHu8Plqs?rlkey=k4fe7xvs76ihzcvufqs4rmg1p&st=nsubccp7&dl=0";

  const [currentFolderUrl, setCurrentFolderUrl] = useState(templatesFolderId);

  const {
    data: templateFiles = [],
    isLoading: templatesLoading,
    error: templatesError,
  } = useDropboxFolder(templatesFolderId);

  const initialFolderUrl = location.state?.folderUrl || "/";
  const initialFolderTitle = location.state?.title || "Root Folder";

  const [currentFolderTitle, setCurrentFolderTitle] =
    useState(initialFolderTitle);

  const [folderHistory, setFolderHistory] = useState([
    { url: initialFolderUrl, title: initialFolderTitle },
  ]);

  const files = templateFiles;
  const isLoading = templatesLoading;
  const error = templatesError;

  const [filteredFiles, setFilteredFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [historyIndex, setHistoryIndex] = useState(0);

  const getFileIcon = (fileName, isFolder = false) => {
    if (isFolder) return <FolderIcon style={{ color: "#f0ad4e" }} />;
    const ext = fileName.toLowerCase().split(".").pop();
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext))
      return <ImageIcon style={{ color: "#17a2b8" }} />;
    if (["mp4", "avi", "mov", "wmv", "mkv", "webm"].includes(ext))
      return <MovieIcon style={{ color: "#dc3545" }} />;
    if (ext === "pdf") return <PictureAsPdfIcon style={{ color: "#dc3545" }} />;
    if (["doc", "docx", "txt", "rtf"].includes(ext))
      return <DescriptionIcon style={{ color: "#007bff" }} />;
    if (["xls", "xlsx", "csv"].includes(ext))
      return <TableChartIcon style={{ color: "#28a745" }} />;
    return <InsertDriveFileIcon style={{ color: "#6c757d" }} />;
  };

  const getFileType = (fileName) => {
    const ext = fileName.toLowerCase().split(".").pop();
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext))
      return "image";
    if (["mp4", "avi", "mov", "wmv", "mkv", "webm"].includes(ext))
      return "video";
    if (ext === "pdf") return "pdf";
    if (["doc", "docx", "txt", "rtf"].includes(ext)) return "document";
    if (["xls", "xlsx", "csv"].includes(ext)) return "spreadsheet";
    return "other";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  };

  const filterFiles = useCallback((fileList, query) => {
    if (!query.trim()) return fileList;
    return fileList.filter((file) =>
      file.name.toLowerCase().includes(query.toLowerCase())
    );
  }, []);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setFilteredFiles(filterFiles(files, query));
  };

  const handleFilePreview = async (file) => {
    setPreviewFile(file);
    setShowPreview(true);
    const fileType = getFileType(file.name);
    if (["image", "video", "pdf", "document"].includes(fileType)) {
      const url = await getFileUrl(file);
      if (url) setFileUrl(url);
    } else {
      setFileUrl(null);
    }
  };

  const getFileUrl = async (file) => {
    try {
      setLoadingPreview(true);
      setPreviewError(null);
      const res = await API.post(
        "/dropbox/file",
        JSON.stringify({ path: file.id, download: false })
      );
      const directLink = res?.data?.download_link;
      if (!directLink) throw new Error("Missing download link");

      const fileType = getFileType(file.name);
      if (["image", "video"].includes(fileType)) {
        return directLink;
      }
      if (fileType === "document" || fileType === "pdf") {
        return `https://docs.google.com/gview?url=${encodeURIComponent(
          directLink
        )}&embedded=true`;
      }
      return null;
    } catch (error) {
      console.error("Preview fetch error:", error);
      setPreviewError("Unable to load file preview");
      return null;
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleFileDownload = async (file) => {
    try {
      const res = await API.post(
        "/dropbox/file",
        JSON.stringify({ path: file.id, download: false })
      );
      const downloadLink = res?.data?.download_link;
      if (downloadLink) {
        const link = document.createElement("a");
        link.href = downloadLink;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error("No download link available");
      }
    } catch (error) {
      alert("Failed to download file");
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewFile(null);
    setFileUrl(null);
    setPreviewError(null);
  };

  const handleBack = () => {
    navigate("/acadamy-dashboard");
  };

  useEffect(() => {
    if (files.length > 0) {
      setFilteredFiles(filterFiles(files, searchQuery));
    } else {
      setFilteredFiles([]);
    }
  }, [files, searchQuery, filterFiles]);

  const handleItemClick = async (file) => {
    if (file[".tag"] === "folder") {
      setCurrentFolderUrl(file.id);
      navigate(`/folder-view/${templatesFolder.id}`, {
        state: {
          folderUrl: file.id,
          title: file.name,
          back: "dictionaries",
        },
      });
    } else {
      await handleFilePreview(file);
    }
  };

  return (
    <>
      <div className="page_head_top">{t("pdf")}</div>

      <Box>
        <div className="row mb-3 row-gap-3">
          <div className="col-xl-4 col-lg-3 col-5 d-flex align-items-center order-1">
            <div className="ms-lg-3">
              {/* <Button
                variant="outline-secondary"
                onClick={handleBack}
                disabled={historyIndex === 0 || location.state?.back === ""}
              >
                <ArrowBackIcon /> Back
              </Button> */}
            </div>
          </div>
          <div className="col-xl-4 col-lg-5 col-7 d-flex justify-content-lg-center justify-content-end order-lg-3 order-2">
            <div className="search-folder-view">
              <Card className="mb-3">
                <Card.Body>
                  <InputGroup>
                    <InputGroup.Text>
                      <SearchIcon />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search files..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </InputGroup>
                </Card.Body>
              </Card>
            </div>
          </div>
          <div className="col-xl-4 col-lg-4 col-12 d-flex justify-content-center align-items-center order-lg-2 order-3">
            <h4 className="mb-0">{t("pdf")}</h4>
          </div>
        </div>
      </Box>

      <Box sx={{ p: 2 }}>
        {(templatesError || error) && (
          <Alert variant="danger">Error loading PDF files</Alert>
        )}
        {templatesLoading || isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p>Loading PDF...</p>
          </div>
        ) : (
          <div className="row">
            {filteredFiles.length === 0 ? (
              <div className="col-md-12 text-center">
                <p>{t("No files found in Dictionaries")}</p>
              </div>
            ) : (
              filteredFiles.map((file, i) => {
                console.log("File item:", file); // Log each file object
                return (
                  <div
                    key={i}
                    className="col-md-4 mb-3 d-flex justify-content-center"
                  >
                    <Card
                      className="h-100 file-item w-100"
                      onClick={() => handleItemClick(file)}
                    >
                      <Card.Body className="d-flex align-items-center">
                        <div className="me-3">
                          {getFileIcon(file.name, file[".tag"] === "folder")}
                        </div>
                        <div className="flex-grow-1">
                          <div
                            className="fw-bold"
                            style={{
                              whiteSpace: "normal",
                              wordBreak: "break-all",
                              overflowWrap: "break-word",
                            }}
                          >
                            {file.name}
                          </div>
                          <div className="text-muted small">
                            {file[".tag"] === "folder"
                              ? "Folder"
                              : formatFileSize(file.size)}
                          </div>
                        </div>
                        {file[".tag"] !== "folder" && (
                          <div className="btn-group">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFilePreview(file);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFileDownload(file);
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </Button>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                );
              })
            )}
          </div>
        )}
      </Box>

      <Modal show={showPreview} onHide={closePreview} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{previewFile?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingPreview && (
            <div className="text-center">
              <Spinner animation="border" />
              <p>Loading preview...</p>
            </div>
          )}
          {previewError && <Alert variant="danger">{previewError}</Alert>}
          {!loadingPreview && !previewError && (
            <>
              {fileUrl && getFileType(previewFile?.name) === "image" && (
                <img
                  src={fileUrl}
                  alt={previewFile.name}
                  className="img-fluid"
                  referrerPolicy="no-referrer"
                />
              )}
              {fileUrl && getFileType(previewFile?.name) === "video" && (
                <video src={fileUrl} controls style={{ width: "100%" }} />
              )}
              {fileUrl && getFileType(previewFile?.name) === "pdf" && (
                <iframe src={fileUrl} width="100%" title="PDF Preview" />
              )}
              {fileUrl && getFileType(previewFile?.name) === "document" && (
                <iframe
                  src={fileUrl}
                  width="100%"
                  height="600px"
                  title="Document Preview"
                />
              )}
              {!fileUrl && (
                <Alert variant="info">
                  Preview not available for this file type. Please download to
                  view the file.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePreview}>
            <CloseIcon /> Close
          </Button>
          <Button
            variant="primary"
            onClick={() => handleFileDownload(previewFile)}
          >
            <DownloadIcon /> Download
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .file-item:hover {
          cursor: pointer;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </>
  );
}
